import { format } from 'date-fns';
import { every } from 'lodash';
import { PricesBySeasons, TimeZoneKindInternal } from '../../../../../data-access/gql-types/graphql';
import { isWeekend } from '../../../../../utils/helpers/date';
import { TariffData } from '../../../types/tariffs';
import { TariffName } from '../../channel-details/optimizer/configuration/types/tariff-kind';
import { heatmapColors } from '../../common/tariff/utils';
import { GetPricingTierParams } from '../types/types';

const checkIfDateIsOffFromWork = (date: Date, staticHolidays: string[], movableHolidays: string[]) => {
  const short = format(date, 'MM-dd');
  const long = format(date, 'yyyy-MM-dd');
  const formattedStaticHolidays = staticHolidays.map((x) => format(new Date(x), 'MM-dd'));

  return isWeekend(date) || formattedStaticHolidays.includes(short) || movableHolidays.includes(long);
};

export const getSeasonPricesByDate = (date: number, seasonPrices: PricesBySeasons) => {
  const { summer, winter, staticHolidays, movableHolidays } = seasonPrices;

  const currentDate = new Date(date);
  const summerFrom = new Date(summer.from);
  const summerTo = new Date(summer.to);
  const winterFrom = new Date(winter.from);
  const winterTo = new Date(winter.to);
  const isOffOfWork = checkIfDateIsOffFromWork(currentDate, staticHolidays, movableHolidays);

  if (currentDate >= summerFrom && currentDate <= summerTo) {
    return isOffOfWork ? summer.offFromWorkDayEntries : summer.workingDayEntries;
  } else if (currentDate >= winterFrom && currentDate <= winterTo) {
    return isOffOfWork ? winter.offFromWorkDayEntries : winter.workingDayEntries;
  }

  return [];
};

export const G_N_COLORS = {
  1: heatmapColors[0],
  2: heatmapColors[3],
  3: heatmapColors[6],
};

const getPricingTier = ({ hoursRanges, hour, wholeHolidaysCheaper, wholeWeekendCheaper }: GetPricingTierParams) => {
  if (wholeWeekendCheaper) return 1;
  if (wholeHolidaysCheaper) return 1;

  return hoursRanges.find((x) => {
    if (x.from <= x.to) return hour >= x.from && hour < x.to;
    return hour >= x.from || hour < x.to;
  })?.pricingTier;
};

export const getColorByHour = (tariffData: TariffData, hour: number, holidays: string[]) => {
  const hoursRanges = tariffData.hoursRanges;
  const wholeWeekendCheaper = tariffData.wholeWeekendCheaper && isWeekend(new Date());
  const wholeHolidaysCheaper = tariffData.wholeHolidaysCheaper && holidays.includes(format(new Date(), 'yyyy-MM-dd'));

  if (every(hoursRanges, (x) => x.timeZoneKind === TimeZoneKindInternal.All)) {
    const pricingTier = getPricingTier({ hoursRanges, hour, wholeHolidaysCheaper, wholeWeekendCheaper });

    return pricingTier ? G_N_COLORS[pricingTier] : G_N_COLORS[3];
  } else {
    const season = getTariffSeason();
    const seasonRanges = [...hoursRanges].filter((x) => x.timeZoneKind === season);
    const pricingTier = getPricingTier({ hoursRanges: seasonRanges, hour, wholeHolidaysCheaper, wholeWeekendCheaper });

    return pricingTier ? G_N_COLORS[pricingTier] : G_N_COLORS[2];
  }
};

export const getTariffSeason = () => {
  const now = new Date();
  const month = now.getMonth() + 1;

  if (month >= 4 && month <= 9) {
    return 'SUMMER'; // 01.04 – 30.09
  } else {
    return 'WINTER'; // 01.10 – 31.03
  }
};

export const getLegendParams = (tariff: TariffName) => {
  if ([TariffName.Dynamic, TariffName.G13S].includes(tariff)) return true;
  if (tariff === TariffName.G11) return null;
  if (tariff === TariffName.G13) return [heatmapColors[0], heatmapColors[3], heatmapColors[6]];

  return [heatmapColors[0], heatmapColors[6]];
};
